import http from '@/utils/http'
import type { TenantInfo } from '@/utils/authStorage'

export type ClientType = 'web' | 'app'

export interface LoginRequest {
  username: string
  password: string
  rememberMe?: boolean
  deviceId?: string
  deviceName?: string
  clientType: ClientType
}

export interface AuthUserInfo {
  id: string
  username: string
  display_name: string
  role: string | null
  roles?: string[]
  permissions: string[]
  available_tenants?: TenantInfo[]
}

export interface LoginResponse {
  accessToken: string
  refreshToken: string
  expiresIn: number
  refreshExpiresIn: number
  user: AuthUserInfo
}

export interface SwitchTenantResponse {
  tenant_id: string
  tenant_name: string
  roles: string[]
  permissions: string[]
}

/** 后端 /api/v1/auth/login 响应 → 前端 LoginResponse 映射。 */
interface BackendLoginBody {
  access_token: string
  refresh_token: string
  expires_in: number
  refresh_expires_in?: number
}

/** 后端 /api/v1/auth/me 响应 → AuthUserInfo 映射。 */
interface BackendMeBody {
  id: string
  username: string
  display_name: string
  role: string | null
  roles?: string[]
  permissions: string[]
  available_tenants?: TenantInfo[]
}

const mapUser = (me: BackendMeBody): AuthUserInfo => ({
  id: me.id,
  username: me.username,
  display_name: me.display_name,
  role: me.role,
  roles: me.roles ?? undefined,
  permissions: me.permissions,
  available_tenants: me.available_tenants ?? [],
})

export const login = async (payload: LoginRequest): Promise<LoginResponse> => {
  const raw = await http.post<BackendLoginBody>(
    '/api/v1/auth/login',
    {
      username: payload.username,
      password: payload.password,
      remember_me: payload.rememberMe ?? false
    },
    { headers: { 'X-Skip-Auth': '1' } }
  )
  const body = raw as unknown as BackendLoginBody
  const meRaw = await http.get<BackendMeBody>('/api/v1/auth/me', {
    headers: { Authorization: `Bearer ${body.access_token}`, 'X-Skip-Auth': '1' }
  })
  const me = meRaw as unknown as BackendMeBody
  return {
    accessToken: body.access_token,
    refreshToken: body.refresh_token,
    expiresIn: body.expires_in,
    // 记住我：7 天；未勾选：24 小时（后端按 remember_me 签发）
    refreshExpiresIn: body.refresh_expires_in ?? 24 * 3600,
    user: mapUser(me)
  }
}

export const refreshToken = async (payload: { refreshToken: string; clientType: ClientType }): Promise<LoginResponse> => {
  const raw = await http.post<{ access_token: string }>(
    '/api/v1/auth/refresh',
    { refresh_token: payload.refreshToken },
    { headers: { 'X-Skip-Auth': '1' } }
  )
  const body = raw as unknown as { access_token: string }
  return {
    accessToken: body.access_token,
    refreshToken: payload.refreshToken,
    expiresIn: 1800,
    // 刷新不续期：0 表示保留现有 refreshExpiresAt（后端 refresh 不延长 refresh token）
    refreshExpiresIn: 0,
    user: { id: '', username: '', display_name: '', role: null, permissions: [] }
  }
}

export const logout = () =>
  http.post('/api/v1/auth/logout', null, {
    headers: { 'X-Skip-Auth': '1' }
  })

export const getProfile = () =>
  http.get<AuthUserInfo>('/api/v1/auth/me') as unknown as Promise<AuthUserInfo>

// ─── 个人中心（T-077 / A6）────────────────────────────────────
// 后端契约（src/server/modules/identity_auth/api/router.py:84,99）：
//   PUT  /api/v1/auth/me              body { display_name }  → 返回更新后的 me（含 roles/permissions）
//   POST /api/v1/auth/change-password body { old_password, new_password } → { success: true }
// ⚠️ 后端 me() **不返回 email/phone**，PUT 也只接受 display_name（契约缺口，见 T-077 journal）。

/** PUT /api/v1/auth/me 的请求体（后端只认 display_name，未知字段被忽略）。 */
export interface UpdateProfileRequest {
  display_name: string
}

/** 更新当前用户个人信息（display_name）→ 返回后端更新后的 me。 */
export const updateProfile = (payload: UpdateProfileRequest) =>
  http.put<BackendMeBody>('/api/v1/auth/me', { display_name: payload.display_name }) as unknown as Promise<AuthUserInfo>

/** POST /api/v1/auth/change-password 的请求体。 */
export interface ChangePasswordRequest {
  old_password: string
  new_password: string
}

/**
 * 修改当前用户密码。
 *
 * 后端契约（既有，未改动）：旧密码错 → 401「旧密码错误」；弱密码 → 400「密码需至少 8 位且包含字母和数字」；
 * 成功 → `{ success: true }`。⚠️ **后端签发的 JWT 无状态、不因改密失效** ⇒ 依契约**无需重新登录**。
 */
export const changePassword = (payload: ChangePasswordRequest) =>
  http.post<{ success: boolean }>('/api/v1/auth/change-password', {
    old_password: payload.old_password,
    new_password: payload.new_password
  }) as unknown as Promise<{ success: boolean }>

// ─── 找回密码（API v5：图形验证码 + 重置验证码 + 一次性令牌）───

export interface CaptchaResponse {
  captcha_id: string
  captcha_image: string // base64 svg
  expires_in: number
}

export interface SendVerificationCodeRequest {
  username: string
  captcha: string
  captcha_id: string
}

export interface SendVerificationCodeResponse {
  success: boolean
  expires_in: number
  /** 仅 SMARTBED_DEBUG=true 时返回（联调用） */
  dev_code?: string
}

export interface VerifyCodeResponse {
  token: string
  expires_in: number
}

/** 获取图形验证码（SVG base64） */
export const getCaptcha = () =>
  http.get<CaptchaResponse>('/api/v1/auth/captcha', {
    headers: { 'X-Skip-Auth': '1' }
  }) as unknown as Promise<CaptchaResponse>

/** 发送重置验证码（debug 模式响应带 dev_code） */
export const sendVerificationCode = (data: SendVerificationCodeRequest) =>
  http.post<SendVerificationCodeResponse>('/api/v1/auth/send-verification-code', data, {
    headers: { 'X-Skip-Auth': '1' }
  }) as unknown as Promise<SendVerificationCodeResponse>

/** 校验重置验证码 → 一次性重置令牌 */
export const verifyCode = (data: { username: string; code: string }) =>
  http.post<VerifyCodeResponse>('/api/v1/auth/verify-code', data, {
    headers: { 'X-Skip-Auth': '1' }
  }) as unknown as Promise<VerifyCodeResponse>

/** 重置密码（一次性令牌 + 新密码） */
export const resetPassword = (data: { token: string; new_password: string }) =>
  http.post<{ success: boolean }>('/api/v1/auth/reset-password', data, {
    headers: { 'X-Skip-Auth': '1' }
  }) as unknown as Promise<{ success: boolean }>

/** 切换当前操作租户身份（POST /api/v1/auth/switch-tenant）。 */
export const switchTenant = (tenantId: string) =>
  http.post<SwitchTenantResponse>('/api/v1/auth/switch-tenant', { tenant_id: tenantId }) as unknown as Promise<SwitchTenantResponse>
